// src/components/legal/TermsPage.js — public /terms page (C1, C3).
import { Link } from "react-router-dom";
import { LegalDocShell } from "./LegalDocShell";

export const TermsPage = () => (
  <LegalDocShell
    docKey="terms"
    title="Terms of Service"
    description="Sahod Terms of Service — the agreement governing use of the Sahod payroll and workforce-management platform."
  >
    <div className="legal-doc__lede">
      These Terms of Service (&ldquo;Terms&rdquo;) govern access to and use of Sahod, the
      payroll and workforce-management platform at <strong>sahod.solutions</strong> (the
      &ldquo;Service&rdquo;), operated by Sahod.Solutions, incorporated in Australia
      (&ldquo;Sahod&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account or using
      the Service, you agree to these Terms, our <Link to="/privacy">Privacy Policy</Link>, and
      &mdash; where you process workforce data &mdash; our{" "}
      <Link to="/dpa">Data Processing Agreement</Link>.
    </div>

    <h2>1. Who may accept</h2>
    <p>
      The Service is for business use. By accepting, you confirm you are at least 18 and
      authorised to bind the business you represent (the &ldquo;Customer&rdquo;). If you lack
      that authority, do not use the Service.
    </p>

    <h2>2. Definitions</h2>
    <ul>
      <li><strong>Customer</strong> &mdash; the business that subscribes to the Service.</li>
      <li>
        <strong>Authorised User</strong> &mdash; an individual the Customer permits to use the
        Service (Superuser, Admin, or Viewer).
      </li>
      <li>
        <strong>Employee Data</strong> &mdash; personal data about the Customer&rsquo;s
        workforce that the Customer inputs into the Service.
      </li>
    </ul>

    <h2>3. The Service and licence</h2>
    <p>
      Subject to these Terms, we grant the Customer a limited, non-exclusive, non-transferable
      right to use the Service for its internal payroll and workforce operations during the
      subscription term. We may improve, change, or discontinue features, and will give
      reasonable notice of materially adverse changes where practicable.
    </p>

    <h2>4. Accounts, roles, and security</h2>
    <p>
      The Customer is responsible for all activity under its account and for keeping credentials
      confidential. Access follows roles: <strong>Superuser</strong> (full access including
      billing), <strong>Admin</strong> (full access except billing), and <strong>Viewer</strong>{" "}
      (read-only). The Customer must promptly remove access for users who should no longer have
      it, and notify us of any unauthorised use.
    </p>

    <h2>5. Customer responsibilities and data</h2>
    <p>
      As between the parties, the Customer is the controller of Employee Data and Sahod is the
      processor, as set out in the <Link to="/dpa">Data Processing Agreement</Link> and{" "}
      <Link to="/privacy">Privacy Policy</Link>. The Customer is solely responsible for:
    </p>
    <ul>
      <li>the accuracy, legality, and completeness of all data it inputs;</li>
      <li>
        having a lawful basis &mdash; including any required employee notice and consent &mdash;
        to provide Employee Data to us;
      </li>
      <li>
        complying with all applicable Philippine labour, tax, and social-contribution laws (SSS,
        PhilHealth, Pag-IBIG, BIR, DOLE);
      </li>
      <li>
        verifying that the statutory contribution and withholding rates and tables applied by
        the Service (including SSS, PhilHealth, Pag-IBIG, and BIR) are current and correct for
        each payroll period before approving, paying, or filing any output, and correctly
        configuring any rate the Service allows the Customer to set;
      </li>
      <li>
        reviewing and approving all payroll outputs before they are paid, filed, or relied upon.
      </li>
    </ul>
    <p>
      The Customer owns its data. We claim no ownership of Employee Data and use it only to
      provide the Service, as described in the DPA.
    </p>

    <h2>6. Acceptable use</h2>
    <p>
      You must not: use the Service unlawfully or to process data you have no right to process;
      attempt to access another customer&rsquo;s data; reverse-engineer, resell, or sublicense
      the Service except as permitted; upload malicious code; or interfere with the
      Service&rsquo;s operation or security.
    </p>

    <h2>7. Fees, billing, and trial</h2>
    <p>
      Pricing is graduated per active employee, as displayed on our website or order form.
      Subscriptions are billed monthly in advance, based on the peak number of active employees
      during the preceding billing cycle. Payments are processed by our third-party payment
      processor; you authorise recurring charges. On payment failure, accounts may be restricted to read-only (Viewer)
      access and are subject to a grace period before suspension, as described in-product. Fees
      are exclusive of taxes and non-refundable except where required by law. Trial terms are as
      stated at sign-up.
    </p>
    <p>
      We may change our fees or pricing structure on at least 30 days&rsquo; notice (by email or
      in-product). A change takes effect from the start of the Customer&rsquo;s next billing
      cycle after the notice period. Continued use of the Service after the change takes effect
      constitutes acceptance; the Customer may cancel before then as described in-product.
    </p>

    <h2>8. Intellectual property</h2>
    <p>
      We and our licensors own all rights in the Service, including software, design, and
      trademarks. The Customer owns its data and grants us a limited licence to host and process
      it solely to provide the Service.
    </p>

    <h2>9. Confidentiality</h2>
    <p>
      Each party will protect the other&rsquo;s confidential information and use it only to
      perform under these Terms, except where disclosure is required by law.
    </p>

    <h2>10. Disclaimer &mdash; Sahod is a tool, not professional advice</h2>
    <div className="legal-doc__caps">
      <p>
        THE SERVICE PROVIDES SOFTWARE TOOLS TO ASSIST WITH PAYROLL. SAHOD DOES NOT PROVIDE LEGAL,
        TAX, ACCOUNTING, OR HR ADVICE. PAYROLL CALCULATIONS DEPEND ON THE DATA AND SETTINGS THE
        CUSTOMER PROVIDES. THE CUSTOMER IS RESPONSIBLE FOR VERIFYING THAT ALL OUTPUTS COMPLY WITH
        APPLICABLE LAW BEFORE RELYING ON THEM. EXCEPT AS REQUIRED BY LAW, THE SERVICE IS PROVIDED
        &ldquo;AS IS&rdquo; WITHOUT WARRANTIES, AND WE DO NOT WARRANT THAT OUTPUTS WILL SATISFY
        EVERY REGULATORY OBLIGATION OR BE UNINTERRUPTED OR ERROR-FREE.
      </p>
    </div>
    <p>
      Statutory contribution and tax tables change from time to time. While we endeavour to keep
      them current, we do not warrant that any rate or table in the Service is accurate or up to
      date for any given period, and the Customer is responsible for verifying them before
      relying on, paying, or filing any output.
    </p>

    <h2>11. Limitation of liability</h2>
    <div className="legal-doc__caps">
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY IS LIABLE FOR INDIRECT, INCIDENTAL,
        OR CONSEQUENTIAL LOSS. OUR TOTAL AGGREGATE LIABILITY ARISING FROM THE SERVICE IS LIMITED
        TO THE FEES PAID BY THE CUSTOMER IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE
        CLAIM. NOTHING EXCLUDES LIABILITY THAT CANNOT BE EXCLUDED BY LAW (INCLUDING UNDER THE
        AUSTRALIAN CONSUMER LAW WHERE IT APPLIES).
      </p>
    </div>

    <h2>12. Indemnity</h2>
    <p>
      The Customer will indemnify us against claims arising from its data, its breach of these
      Terms, or its failure to comply with applicable law in its use of the Service.
    </p>

    <h2>13. Term, suspension, and termination</h2>
    <p>
      These Terms apply while the Customer uses the Service. Either party may terminate as
      described in-product or for material breach. We may suspend access for non-payment or for
      activity that threatens the security or integrity of the Service. On termination, the
      Customer may export its data within a reasonable window, after which we delete it as
      described in the <Link to="/dpa">DPA</Link>, subject to legally required retention.
    </p>

    <h2>14. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. We will post the current version at
      sahod.solutions/terms and, for material changes, notify you through the Service or by
      email. Continued use after the effective date constitutes acceptance.
    </p>

    <h2>15. Force majeure</h2>
    <p>
      Neither party is liable for any delay or failure to perform (other than an obligation to
      pay fees for Service already provided) caused by events beyond its reasonable control,
      including acts of God, natural disaster, fire, flood, epidemic, war, civil unrest,
      government action, labour dispute, or the failure, interruption, or degradation of the
      internet, telecommunications, hosting, payment, email, or other third-party services on
      which the Service depends (including our cloud infrastructure, payment, and email providers). The
      affected party will use reasonable efforts to resume performance promptly.
    </p>

    <h2>16. General</h2>
    <ul>
      <li>
        <strong>Entire agreement.</strong> These Terms, together with the Privacy Policy, the
        DPA, and the plan or order details presented at sign-up, are the entire agreement between
        the parties on their subject matter and supersede all prior discussions, proposals, or
        representations.
      </li>
      <li>
        <strong>Severability.</strong> If any provision is held invalid or unenforceable, it will
        be modified to the minimum extent necessary to make it enforceable; if it cannot be, it
        will be severed, and the remaining provisions remain in full force.
      </li>
      <li>
        <strong>No waiver.</strong> A party&rsquo;s failure or delay in enforcing any right or
        provision is not a waiver of it.
      </li>
      <li>
        <strong>Assignment.</strong> The Customer may not assign or transfer these Terms without
        our prior written consent. We may assign them to an affiliate or in connection with a
        merger, acquisition, or sale of assets. Subject to this, the Terms bind and benefit the
        parties and their permitted successors.
      </li>
      <li>
        <strong>Relationship.</strong> The parties are independent contractors; nothing in these
        Terms creates a partnership, joint venture, agency, or employment relationship.
      </li>
      <li>
        <strong>Survival.</strong> Provisions that by their nature should survive termination
        &mdash; including Sections 5, 8 to 12, and this Section &mdash; survive.
      </li>
    </ul>

    <h2>17. Governing law and disputes</h2>
    <p>
      These Terms are governed by the laws of the State or Territory of Australia in which Sahod
      is registered, and the parties submit to the courts of that place, without prejudice to
      mandatory protections available to data subjects and customers under Philippine law.
    </p>

    <h2>18. Contact</h2>
    <p>Sahod.Solutions (Australia) &middot; support@sahod.solutions</p>
  </LegalDocShell>
);
